import * as React from 'react';
import { z } from 'zod';
import { Card, CardBody, CardHeader, CardTitle } from './Card';
import { Field } from './Field';
import { FormField, type FormSchema, type ZodForm } from './Form';
import { Input } from './Input';
import { Stack } from './Layout';

export interface ClientInfoCardProps {
  clientName: string;
  onClientNameChange: (name: string) => void;
  clientEmail: string;
  onClientEmailChange: (email: string) => void;
  /** Both fields lock; the sent invoice's client cannot be renamed. */
  isReadOnly?: boolean;
}

/**
 * The "Client" card in the invoice editor's rail: who the invoice is for and
 * where it is sent.
 *
 *   <ClientInfoCard clientName={name} onClientNameChange={setName}
 *     clientEmail={email} onClientEmailChange={setEmail} />
 */
export function ClientInfoCard({
  clientName,
  onClientNameChange,
  clientEmail,
  onClientEmailChange,
  isReadOnly = false,
}: ClientInfoCardProps) {
  return (
    <Card padding="md" radius="md" className="overflow-hidden">
      <CardHeader divided tone="muted">
        <CardTitle>Client</CardTitle>
      </CardHeader>
      <CardBody>
        <Stack gap={4}>
          <Field label="Client Name">
            <Input
              type="text"
              placeholder="Enter client name"
              value={clientName}
              onChange={(e) => onClientNameChange(e.target.value)}
              disabled={isReadOnly}
            />
          </Field>
          <Field label="Client Email">
            <Input
              type="email"
              placeholder="Enter client email"
              value={clientEmail}
              onChange={(e) => onClientEmailChange(e.target.value)}
              disabled={isReadOnly}
            />
          </Field>
        </Stack>
      </CardBody>
    </Card>
  );
}

/**
 * Who the invoice is billed to, spread into the editor's schema. Both are
 * needed to send one: the name heads the invoice, and the email is where it
 * goes — the backend rejects a create without either.
 */
export const clientInfoShape = {
  clientName: z.string().trim().min(1, "Enter the client's name."),
  clientEmail: z
    .string()
    .trim()
    .min(1, "Enter the client's email address.")
    .email('Enter a valid email address.'),
};

/** The shape as a schema, for the cast below. */
type ClientInfoSchema = z.ZodObject<typeof clientInfoShape>;

export interface ClientFieldsCardProps<S extends FormSchema> {
  /** The editor's form; its schema spreads `clientInfoShape`. */
  form: ZodForm<S>;
  isReadOnly?: boolean;
}

/**
 * The same card, bound to a form: the editor's version, where both fields
 * carry the marker and the message the schema gives them.
 *
 *   <ClientFieldsCard form={form} />
 *
 * `ClientInfoCard` stays for the read-only invoice view, which has no form.
 */
export function ClientFieldsCard<S extends FormSchema>({
  form,
  isReadOnly = false,
}: ClientFieldsCardProps<S>) {
  // See LotFormFields: the caller's schema spreads this shape, which
  // TypeScript cannot see through the generic.
  const f = form as unknown as ZodForm<ClientInfoSchema>;
  // Nothing is required of you on a card you cannot type into.
  const marker = isReadOnly ? false : undefined;
  return (
    <Card padding="md" radius="md" className="overflow-hidden">
      <CardHeader divided tone="muted">
        <CardTitle>Client</CardTitle>
      </CardHeader>
      <CardBody>
        <Stack gap={4}>
          <FormField
            form={f}
            name="clientName"
            label="Client Name"
            required={marker}
          >
            {(field) => (
              <Input
                type="text"
                placeholder="Enter client name"
                {...field}
                disabled={isReadOnly}
              />
            )}
          </FormField>
          <FormField
            form={f}
            name="clientEmail"
            label="Client Email"
            required={marker}
          >
            {(field) => (
              <Input
                type="email"
                placeholder="Enter client email"
                {...field}
                disabled={isReadOnly}
              />
            )}
          </FormField>
        </Stack>
      </CardBody>
    </Card>
  );
}
